import React, { useContext, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { clearUsers, ritzDataContext, selectProjectById, selectSudoProjectAccessKey } from '@moderepo/mode-data-state-ritz';
import { BaseCompAction, capitalizeAll, getErrorMessage, SlideOutComp } from '@moderepo/mode-app-components';
import { DialogResponse, DialogResponseCode, DialogType, hideSlideOutPanel, modeUIContext, showDialog, showErrorNotification,
    showSuccessNotification } from '@moderepo/mode-ui-state';
import { makeStyles, Theme } from '@material-ui/core';
import clsx from 'clsx';
import { ApiError, User } from '@moderepo/mode-internal-apis';
import { AppAPI } from '@moderepo/mode-apis';
import { useRitzTranslation } from '../../utils/customHooks';
import { UserInfoContainer } from './UserInfoContainer';
import { RouteHelper } from '../routes/RouteHelper';
import { useSelectUser } from '../../utils/dataStateHooks';


const useStyle = makeStyles((theme: Theme) => {
    return {
        secondaryAction: {
            '& .icon': {
                color: theme.palette.primary.main,
            },
            '&.remove': {
                '& .icon': {
                    color: theme.palette.secondary.main,
                },
            },
        },
    };
});


interface UserInfoPreviewContainerProps {
    readonly projectId: number;
    readonly userId: number | undefined;
}


/**
 * This Component is used for displaying UserInfo on the preview panel for preview. It does not show all details user info, only the important details
 */
export const UserInfoPreviewContainer = (props: UserInfoPreviewContainerProps) => {
    const navigate = useNavigate();
    const { projectId } = props;
    const { state:dataState, dispatch:dataDispatch } = useContext(ritzDataContext);
    const { dispatch:uiDispatch } = useContext(modeUIContext);
    const project = selectProjectById(dataState, projectId);
    const projectKey = selectSudoProjectAccessKey(dataState, props.projectId);
    const selectedUser = useSelectUser(projectId, props.userId);
    const { trans } = useRitzTranslation(project);
    const isNewUser = props.userId === undefined;
    const routeHelper = RouteHelper.getInstance(navigate);
    const classes = useStyle();



    /**
     * Unselect the selected user and update the URL
     */
    const unselectUser = useCallback(() => {
        uiDispatch(hideSlideOutPanel());
    }, [uiDispatch]);



    const onViewUserDetails = useCallback((userId: number) => {
        routeHelper.gotoUserPage({ projectId, userId });
        uiDispatch(hideSlideOutPanel());
    }, [routeHelper, projectId, uiDispatch]);


    /**
     * onDelete user button clicked
     */
    const onDeleteUser = useCallback((user: User, errorMessage: string | undefined) => {

        // Confirm before deleting user. Because deleting a user can be dangerous, we will ask the agent to enter the user's name
        uiDispatch(showDialog({
            type       : DialogType.INPUT,
            title      : 'dialog.generic_confirm_delete.title',
            titleData  : { object_name: user.name || `#${user.id.toString()}`, object_term: 'terms.user' },
            message    : 'dialog.generic_confirm_delete.message',
            messageData:
                {
                    object_name : user.name || `#${user.id.toString()}`,
                    object_term : 'terms.user',
                    object_field: user.name ? 'label.user_name' : 'label.id',
                },
            positiveButton: 'label.delete',
            negativeButton: 'label.cancel',
            input         : {
                type           : 'text',
                name           : user.name ? 'label.user_name' : 'label.id',
                error          : errorMessage,
                placeholder    : 'input.delete_object_name.hint_text',
                translationData: {
                    object_term : 'terms.user',
                    object_field: user.name ? 'label.user_name' : 'label.id',
                },
                required: true,
            },
            onClose: async (response?: DialogResponse): Promise<void> => {
                if (response && response.code === DialogResponseCode.POSITIVE) {
                    const userResponse = response.input ? response.input.trim() : undefined;

                    if (!userResponse) {
                        // user response is not provided. Call this function again to show the delete user dialog with the new error
                        onDeleteUser(user, 'error.input.generic.empty');
                    } else if (user.name && userResponse !== user.name) {
                        // Name does not match. Call this function again to show the delete user dialog with the new error
                        onDeleteUser(user, 'error.input.generic.invalid');
                    } else if (!user.name && Number(userResponse) !== user.id) {
                        // ID does not match AND user does not have a name.
                        // Call this function again to show the delete user dialog with the new error
                        onDeleteUser(user, 'error.input.generic.invalid');
                    } else {
                        try {
                            await AppAPI.getInstance().deleteUser(user.id, projectKey?.token);

                            uiDispatch(showSuccessNotification('notif.generic_item_deleted_successfully', {
                                term: capitalizeAll(trans('terms.user')),
                            }));
                            unselectUser();

                            // Clear all the cached user data to force UI to reload user list
                            await dataDispatch(clearUsers(props.projectId));
                        } catch (error) {
                            // Show error using notification
                            uiDispatch(showErrorNotification(getErrorMessage((error as ApiError).code)));

                            // Call this function again to show the delete user dialog
                            onDeleteUser(user, undefined);
                        }
                    }
                }
            },
        }));
    }, [dataDispatch, projectKey, props.projectId, trans, uiDispatch, unselectUser]);


    const onNewUserCanceled = useCallback(() => {
        unselectUser();
    }, [unselectUser]);



    const onNewUserCreated = useCallback(() => {
        unselectUser();
    }, [unselectUser]);



    /**
     * use useMemo to create the the newUserSettings so that this doesn't get created on every re-render
     */
    const newUserSettings = useMemo(() => {
        return {
            onNewUserCreated,
            onNewUserCanceled,
        };
    }, [onNewUserCreated, onNewUserCanceled]);


    const primaryActions = useMemo(() => {
        if (!isNewUser) {
            const actions: BaseCompAction[] = [{
                label  : capitalizeAll(trans('label.view_user_details')),
                onClick: () => {
                    onViewUserDetails(props.userId as number);
                },
            }];
            return actions;
        }

        return undefined;
    }, [isNewUser, onViewUserDetails, props.userId, trans]);


    const secondaryActions = useMemo(() => {
        if (!isNewUser && selectedUser) {
            const actions: BaseCompAction[] = [{
                className: clsx(classes.secondaryAction, 'remove'),
                label    : capitalizeAll(trans('label.delete_object', { term: trans('terms.user') })),
                icon     : 'delete',
                onClick  : () => {
                    onDeleteUser(selectedUser, undefined);
                },
            }];
            return actions;
        }

        return undefined;
    }, [classes.secondaryAction, isNewUser, onDeleteUser, selectedUser, trans]);


    const slideOutCompContent = useMemo(() => {
        return (
            <UserInfoContainer
                className="transparent"
                infoCompProps={{
                    formProps: {
                        className: 'single-column',
                    },
                }}
                projectId={projectId}
                userId={props.userId}
                newUserSettings={
                    isNewUser ? newUserSettings : undefined
                }
            />
        );
    }, [isNewUser, newUserSettings, projectId, props.userId]);


    return (
        <SlideOutComp
            primaryActions={primaryActions}
            secondaryActions={secondaryActions}
            compContent={slideOutCompContent}
            closable={!isNewUser}
            contentContainerProps={{
                className: 'slide-out-comp-container',
            }}
        />
    );
};
